import type {
  WorkbookDefinition,
  WorkbookSinkTargetColumn,
  WorkbookSinkTarget,
  WorkbookSinkTargetOption,
  WorkbookSinkTargetRef,
  WorkbookSinkMapping,
  WorkbookSinkValue,
} from '@erp/shared-types';
import { isColumnarSheet } from '@erp/shared-types';

export type SinkTargetColumn = WorkbookSinkTargetColumn;

/** A table a Data sink can write: a Data Source Link's ingest table or a Stateful table. */
export type SinkTarget = WorkbookSinkTargetOption;

export type SinkTargetKind = WorkbookSinkTarget['kind'];
export type SinkWriteMode = 'snapshot' | 'upsert';

/** The target's kind and id, as `sinkTargets` and `sinkTargetColumns` name it. */
export function targetRef(target: WorkbookSinkTarget): WorkbookSinkTargetRef {
  return target.kind === 'data-source-link'
    ? { kind: target.kind, id: target.linkId }
    : { kind: target.kind, id: target.tableDefinitionId };
}

/** A target for a chosen table; a Stateful table starts out merging by key. */
export function targetFromRef(ref: WorkbookSinkTargetRef): WorkbookSinkTarget {
  return ref.kind === 'data-source-link'
    ? { kind: ref.kind, linkId: ref.id }
    : { kind: ref.kind, tableDefinitionId: ref.id, writeMode: 'upsert' };
}

/** A picker value naming one table. */
export function targetKey(ref: WorkbookSinkTargetRef): string {
  return `${ref.kind}:${ref.id}`;
}

/** The option the target points at. */
export function findSinkTarget(
  options: readonly SinkTarget[],
  target: WorkbookSinkTarget | undefined,
): SinkTarget | undefined {
  if (!target) return undefined;
  const key = targetKey(targetRef(target));
  return options.find((option) => targetKey(option) === key);
}

/** The options of one kind, in order. */
export function sinkTargetsOfKind(
  options: readonly SinkTarget[],
  kind: SinkTargetKind,
): SinkTarget[] {
  return options.filter((option) => option.kind === kind);
}

/** Names compare ignoring case, underscores, spaces and hyphens. */
export function autoMapKey(name: string): string {
  return name.toLowerCase().replace(/[\s_-]+/g, '');
}

/** The Worksheet columns a sink can read: its Columns, else its Result's. */
export function sinkSheetColumns(
  definition: Pick<WorkbookDefinition, 'sheets'>,
  sheetId: string | undefined,
  resultColumns: readonly string[] | undefined,
): string[] {
  const sheet = definition.sheets.find((item) => item.id === sheetId);
  if (!sheet) return [];
  return isColumnarSheet(sheet)
    ? sheet.columns.map((column) => column.name)
    : [...(resultColumns ?? [])];
}

/** The mapping for `targetColumn`. */
export function mappingFor(
  mappings: readonly WorkbookSinkMapping[],
  targetColumn: string,
): WorkbookSinkValue | undefined {
  return mappings.find((mapping) => mapping.targetColumn === targetColumn)
    ?.value;
}

/** The mappings with `targetColumn` set to `value`, or unmapped when undefined. */
export function withMapping(
  mappings: readonly WorkbookSinkMapping[],
  targetColumn: string,
  value: WorkbookSinkValue | undefined,
): WorkbookSinkMapping[] {
  const rest = mappings.filter(
    (mapping) => mapping.targetColumn !== targetColumn,
  );
  if (!value) return rest;
  const index = mappings.findIndex(
    (mapping) => mapping.targetColumn === targetColumn,
  );
  const next: WorkbookSinkMapping = { targetColumn, value };
  return index < 0
    ? [...rest, next]
    : [...rest.slice(0, index), next, ...rest.slice(index)];
}

/**
 * The mappings with every still-unmapped target column mapped to the
 * Worksheet column whose name matches. Mappings already made are kept, except
 * those naming a column the target no longer has.
 */
export function autoMapped(
  mappings: readonly WorkbookSinkMapping[],
  targetColumns: readonly SinkTargetColumn[],
  sheetColumns: readonly string[],
): WorkbookSinkMapping[] {
  const byKey = new Map<string, string>();
  for (const column of sheetColumns) {
    const key = autoMapKey(column);
    if (!byKey.has(key)) byKey.set(key, column);
  }
  const added = targetColumns.flatMap((target): WorkbookSinkMapping[] => {
    if (mappingFor(mappings, target.name)) return [];
    const column = byKey.get(autoMapKey(target.name));
    return column
      ? [{ targetColumn: target.name, value: { kind: 'column', column } }]
      : [];
  });
  const known = new Set(targetColumns.map((column) => column.name));
  return [
    ...mappings.filter((mapping) => known.has(mapping.targetColumn)),
    ...added,
  ];
}

/** What is wrong with a sink's mappings before it writes. */
export type SinkIssue =
  | { kind: 'missing-key'; targetColumn: string }
  | { kind: 'unknown-column'; targetColumn: string; column: string }
  | { kind: 'unknown-target'; targetColumn: string };

export function sinkIssues(
  mappings: readonly WorkbookSinkMapping[],
  targetColumns: readonly SinkTargetColumn[],
  sheetColumns: readonly string[],
): SinkIssue[] {
  const known = new Set(targetColumns.map((column) => column.name));
  const sheet = new Set(sheetColumns);
  return [
    ...targetColumns.flatMap((column): SinkIssue[] =>
      column.isKey && !mappingFor(mappings, column.name)
        ? [{ kind: 'missing-key', targetColumn: column.name }]
        : [],
    ),
    ...mappings.flatMap((mapping): SinkIssue[] => {
      if (!known.has(mapping.targetColumn))
        return [{ kind: 'unknown-target', targetColumn: mapping.targetColumn }];
      return mapping.value.kind === 'column' && !sheet.has(mapping.value.column)
        ? [
            {
              kind: 'unknown-column',
              targetColumn: mapping.targetColumn,
              column: mapping.value.column,
            },
          ]
        : [];
    }),
  ];
}

/** How many of the target's columns have a mapping. */
export function mappedCount(
  mappings: readonly WorkbookSinkMapping[],
  targetColumns: readonly SinkTargetColumn[],
): number {
  return targetColumns.filter((column) => mappingFor(mappings, column.name))
    .length;
}

/**
 * Whether a sink can write now: its Worksheet's Result is Current, its target's
 * columns are known, and it has mappings with no issue (the server refuses a
 * delivery with any).
 */
export function sinkWritable({
  state,
  targetColumns,
  mappingCount,
  issues,
}: {
  state: string;
  targetColumns: readonly SinkTargetColumn[] | undefined;
  mappingCount: number;
  issues: readonly SinkIssue[];
}): boolean {
  return (
    state === 'current' &&
    targetColumns !== undefined &&
    mappingCount > 0 &&
    issues.length === 0
  );
}
